import React from 'react';
import { Box, ButtonBase, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  DescriptionOutlined,
  ErrorOutline,
  MoreVert,
  SlideshowOutlined,
} from '@mui/icons-material';
import { IconTile } from '../pagekit';

/** Hidden from sight, still read by assistive technology. */
const visuallyHidden = {
  position: 'absolute',
  // Strings: in sx a bare 1 means 100%, not 1px.
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Chip summarizing generated fields: "All 8 sections filled", "2 of 8 need input", or, when every
 * section is filled but template variables (e.g. {{customer.name}}) could not be resolved,
 * "All 8 sections filled · 2 details missing".
 * @param {object} props - Props.
 * @param {{total: number, filled: number, needsInput: number, unresolvedVariables?: string[]}|null} props.summary - Field summary.
 * @returns {JSX.Element|null} Chip.
 */
export function FieldStatusChip({ summary }) {
  if (!summary || !summary.total) return null;
  if (summary.needsInput > 0) {
    return (
      <Chip
        size="small"
        color="warning"
        icon={<ErrorOutline />}
        label={`${summary.needsInput} of ${summary.total} need input`}
      />
    );
  }
  const missing = summary.unresolvedVariables?.length || 0;
  if (missing > 0) {
    return (
      <Tooltip title={`Not filled: ${summary.unresolvedVariables.join(', ')}`}>
        <Chip
          size="small"
          color="warning"
          icon={<ErrorOutline />}
          label={`All ${summary.total} sections filled · ${missing} detail${missing === 1 ? '' : 's'} missing`}
        />
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      color="success"
      icon={<CheckCircleOutline />}
      label={`All ${summary.total} sections filled`}
    />
  );
}

/**
 * Document card used by the Documents page and the project Documents tab. The title is the
 * card's button: it opens the preview, and its hit area is stretched over the whole card so a
 * click anywhere opens it too. The actions menu button and any chips sit above that overlay as
 * separate controls (no interactive element is nested inside another).
 * @param {object} props - Props.
 * @param {{id: string, name: string, createdAt?: string}} props.document - Document.
 * @param {string} props.snippet - Precomputed text snippet.
 * @param {{total: number, filled: number, needsInput: number}|null} [props.fieldSummary] - Field fill summary.
 * @param {boolean} [props.isPresentation] - Deck icon instead of document icon.
 * @param {string} props.metaLine - Secondary line under the name (date, counts).
 * @param {React.ReactNode} [props.chips] - Extra chips (customer, project).
 * @param {boolean} [props.highlighted] - Emphasize (e.g. just generated).
 * @param {string|null} [props.busyLabel] - Shows a spinner with this label (e.g. "Exporting PDF").
 * @param {function(): void} props.onOpen - Open the preview.
 * @param {function(React.MouseEvent<HTMLElement>): void} props.onMenuOpen - Open the actions menu.
 * @param {string} [props.id] - DOM id (for scroll-to).
 * @returns {JSX.Element} Card.
 */
export default function DocumentCard({
  document,
  snippet,
  fieldSummary = null,
  isPresentation = false,
  metaLine,
  chips = null,
  highlighted = false,
  busyLabel = null,
  onOpen,
  onMenuOpen,
  id,
}) {
  return (
    <Box
      id={id}
      component="article"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        minWidth: 0,
        p: 2,
        bgcolor: highlighted ? 'var(--q-accent-tint)' : 'background.paper',
        border: '1px solid',
        borderColor: highlighted ? 'var(--q-accent)' : 'divider',
        borderRadius: 'var(--q-radius-card)',
        boxShadow: highlighted ? '0 0 0 4px var(--q-focus-halo)' : 'none',
        transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 200ms ease',
        '&:hover': { borderColor: highlighted ? 'var(--q-accent)' : 'var(--q-border-hover)', boxShadow: 'var(--q-shadow-md)' },
        // Controls above the stretched title overlay.
        '& .document-card-raised': { position: 'relative', zIndex: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
        <IconTile tone={isPresentation ? 'neutral' : 'accent'} size={36}>
          {isPresentation ? <SlideshowOutlined /> : <DescriptionOutlined />}
        </IconTile>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h3" sx={{ m: 0, fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>
            <ButtonBase
              className="document-card-open"
              onClick={onOpen}
              disableRipple
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                // Static so the ::after overlay is positioned against the card, not the button.
                position: 'static',
                textAlign: 'left',
                font: 'inherit',
                color: 'text.primary',
                borderRadius: 0.5,
                // Stretch the hit area over the whole card.
                '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: 'var(--q-radius-card)', cursor: 'pointer' },
                '&:focus-visible': { outline: 'none' },
                '&:focus-visible::after': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 2 },
              }}
            >
              <Box component="span" sx={visuallyHidden}>Preview </Box>
              {document.name}
            </ButtonBase>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {metaLine}
          </Typography>
        </Box>
        <Tooltip title="Document actions">
          <span className="document-card-raised">
            <IconButton
              size="small"
              aria-label={`Actions for ${document.name}`}
              aria-haspopup="menu"
              disabled={Boolean(busyLabel)}
              onClick={onMenuOpen}
              sx={{ mt: -0.5, mr: -0.75, color: 'text.secondary' }}
            >
              {busyLabel ? <CircularProgress size={18} /> : <MoreVert fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '3.9em',
          overflowWrap: 'anywhere',
        }}
      >
        {snippet || 'This document is empty.'}
      </Typography>

      {(chips || fieldSummary || busyLabel) && (
        <Box className="document-card-raised" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 'auto', alignSelf: 'flex-start', maxWidth: '100%' }}>
          <FieldStatusChip summary={fieldSummary} />
          {chips}
          {busyLabel && (
            <Typography variant="caption" color="text.secondary" role="status">
              {busyLabel}…
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
